import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getBook } from '@/services/libraryClient';
import AvailabilityPill from '@/components/library/AvailabilityPill';
import RecordMeta from '@/components/library/RecordMeta';
import CiteModal from '@/components/library/CiteModal';
import Breadcrumbs from '@/components/library/Breadcrumbs';
import { ArrowLeft, AlertCircle, FileText } from 'lucide-react';

const LibraryBookDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showCiteModal, setShowCiteModal] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const { data: book, isLoading, isError, error } = useQuery({
    queryKey: ['book', id],
    queryFn: () => getBook(id!),
    enabled: !!id,
  });

  const getInitials = (title: string) => {
    const words = title.split(' ').filter(w => w.length > 2);
    return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
  };

  const isLongDescription = book?.description && book.description.length > 500;
  const displayDescription = showFullDescription || !isLongDescription
    ? book?.description
    : book?.description?.substring(0, 500) + '…';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading book details...</p>
        </div>
      </div>
    );
  }

  if (isError || !book) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Book Not Found</h2>
          <p className="text-gray-600 mb-6">
            {error instanceof Error ? error.message : "The book you're looking for could not be found."}
          </p>
          <button
            onClick={() => navigate('/library')}
            className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Library & Publications', href: '/library' },
    { label: 'Catalogue', href: '/library/catalogue' },
    { label: book?.title || 'Book Details' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs & Back Button */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          {book && <Breadcrumbs items={breadcrumbItems} />}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Catalogue</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Cover Image */}
              <div className="md:col-span-1">
                <div className="relative bg-gray-100 aspect-[2/3] rounded-lg overflow-hidden shadow-lg">
                  {book.cover_url ? (
                    <img
                      src={book.cover_url}
                      alt={`Cover of ${book.title}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`${book.cover_url ? 'hidden' : ''} absolute inset-0 flex items-center justify-center bg-gradient-to-br from-green-100 to-green-50`}>
                    <span className="text-6xl font-bold text-green-600 opacity-40">
                      {getInitials(book.title)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Book Info */}
              <div className="md:col-span-2">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {book.title}
                </h1>
                
                <p className="text-xl text-gray-700 mb-4">
                  {book.authors.join(', ')}
                  {book.year && ` (${book.year})`}
                </p>

                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <AvailabilityPill status={book.status} />
                  {book.call_number && (
                    <span className="text-sm text-gray-600 font-mono">
                      {book.call_number}
                    </span>
                  )}
                </div>

                {book.location && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-gray-500 mb-1">Location</p>
                    <p className="text-gray-900">{book.location}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowCiteModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <FileText className="w-4 h-4" />
                    Cite
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Description */}
            <div className="lg:col-span-2">
              {book.description && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {displayDescription}
                    </p>
                    {isLongDescription && (
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="mt-4 text-green-600 hover:text-green-700 font-medium focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
                      >
                        {showFullDescription ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {book.notes && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Notes</h2>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {book.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-4">
                <RecordMeta book={book} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cite Modal */}
      <CiteModal
        book={book}
        isOpen={showCiteModal}
        onClose={() => setShowCiteModal(false)}
      />
    </div>
  );
};

export default LibraryBookDetailPage;
